// src/pages/CRM/SAF/sfaEnums.js

// Opportunity Stages
export const OpportunityStage = {
  PROSPECTING: 'PROSPECTING',
  QUALIFICATION: 'QUALIFICATION',
  NEEDS_ANALYSIS: 'NEEDS_ANALYSIS',
  VALUE_PROPOSITION: 'VALUE_PROPOSITION',
  PROPOSAL: 'PROPOSAL',
  NEGOTIATION: 'NEGOTIATION',
  CLOSED_WON: 'CLOSED_WON',
  CLOSED_LOST: 'CLOSED_LOST'
};

export const OpportunityStageLabels = {
  PROSPECTING: 'Prospecting',
  QUALIFICATION: 'Qualification',
  NEEDS_ANALYSIS: 'Needs Analysis',
  VALUE_PROPOSITION: 'Value Proposition',
  PROPOSAL: 'Proposal/Quote',
  NEGOTIATION: 'Negotiation',
  CLOSED_WON: 'Closed Won',
  CLOSED_LOST: 'Closed Lost'
};

export const OpportunityStageColors = {
  PROSPECTING: '#9e9e9e',
  QUALIFICATION: '#2196f3',
  NEEDS_ANALYSIS: '#03a9f4',
  VALUE_PROPOSITION: '#00bcd4',
  PROPOSAL: '#ff9800',
  NEGOTIATION: '#ff5722',
  CLOSED_WON: '#4caf50',
  CLOSED_LOST: '#f44336'
};

export const StageProbabilities = {
  PROSPECTING: 10,
  QUALIFICATION: 20,
  NEEDS_ANALYSIS: 40,
  VALUE_PROPOSITION: 60,
  PROPOSAL: 75,
  NEGOTIATION: 90,
  CLOSED_WON: 100,
  CLOSED_LOST: 0
};

// Pipeline stages (excluding closed)
export const PipelineStages = [
  OpportunityStage.PROSPECTING,
  OpportunityStage.QUALIFICATION,
  OpportunityStage.NEEDS_ANALYSIS,
  OpportunityStage.VALUE_PROPOSITION,
  OpportunityStage.PROPOSAL,
  OpportunityStage.NEGOTIATION
];

// All stages in order
export const AllStages = [
  OpportunityStage.PROSPECTING,
  OpportunityStage.QUALIFICATION,
  OpportunityStage.NEEDS_ANALYSIS,
  OpportunityStage.VALUE_PROPOSITION,
  OpportunityStage.PROPOSAL,
  OpportunityStage.NEGOTIATION,
  OpportunityStage.CLOSED_WON,
  OpportunityStage.CLOSED_LOST
];

// Opportunity Types
export const OpportunityType = {
  NEW_BUSINESS: 'NEW_BUSINESS',
  EXISTING_BUSINESS: 'EXISTING_BUSINESS',
  RENEWAL: 'RENEWAL',
  UPSELL: 'UPSELL',
  CROSS_SELL: 'CROSS_SELL'
};

export const OpportunityTypeLabels = {
  NEW_BUSINESS: 'New Business',
  EXISTING_BUSINESS: 'Existing Business',
  RENEWAL: 'Renewal',
  UPSELL: 'Upsell',
  CROSS_SELL: 'Cross-Sell'
};

// Forecast Categories
export const ForecastCategory = {
  PIPELINE: 'PIPELINE',
  BEST_CASE: 'BEST_CASE',
  COMMIT: 'COMMIT',
  CLOSED: 'CLOSED',
  OMITTED: 'OMITTED'
};

export const ForecastCategoryLabels = {
  PIPELINE: 'Pipeline',
  BEST_CASE: 'Best Case',
  COMMIT: 'Commit',
  CLOSED: 'Closed',
  OMITTED: 'Omitted'
};

export const ForecastCategoryColors = {
  PIPELINE: '#2196f3',
  BEST_CASE: '#ff9800',
  COMMIT: '#4caf50',
  CLOSED: '#9c27b0',
  OMITTED: '#9e9e9e'
};

// Quote Statuses
export const QuoteStatus = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  SENT: 'SENT',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED'
};

export const QuoteStatusLabels = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  SENT: 'Sent',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired'
};

export const QuoteStatusColors = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  SENT: 'primary',
  ACCEPTED: 'success',
  REJECTED: 'error',
  EXPIRED: 'default'
};

// Product Types
export const ProductType = {
  PRODUCT: 'PRODUCT',
  SERVICE: 'SERVICE',
  SUBSCRIPTION: 'SUBSCRIPTION',
  ONE_TIME: 'ONE_TIME',
  BUNDLE: 'BUNDLE'
};

export const ProductTypeLabels = {
  PRODUCT: 'Product',
  SERVICE: 'Service',
  SUBSCRIPTION: 'Subscription',
  ONE_TIME: 'One-Time',
  BUNDLE: 'Bundle'
};

// Product Statuses
export const ProductStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  DISCONTINUED: 'DISCONTINUED'
};

export const ProductStatusLabels = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DISCONTINUED: 'Discontinued'
};

export const ProductStatusColors = {
  ACTIVE: 'success',
  INACTIVE: 'default',
  DISCONTINUED: 'error'
};

// Target Periods
export const TargetPeriod = {
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  YEARLY: 'YEARLY'
};

export const TargetPeriodLabels = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Yearly'
};

// Visibility Scope
export const VisibilityScope = {
  PRIVATE: 'PRIVATE',
  TEAM: 'TEAM',
  ORGANIZATION: 'ORGANIZATION'
};

export const VisibilityScopeLabels = {
  PRIVATE: 'Private (Only Me)',
  TEAM: 'Team (My Sales Team)',
  ORGANIZATION: 'Organization (Everyone)'
};

export const VisibilityScopeColors = {
  PRIVATE: 'default',
  TEAM: 'info',
  ORGANIZATION: 'success'
};

// View Filter Options (for filtering opportunities by visibility)
export const ViewFilterOptions = [
  { value: 'all', label: 'All Opportunities' },
  { value: 'my', label: 'My Opportunities' },
  { value: 'team', label: "My Team's Opportunities" }
];

// Team Member Roles
export const TeamMemberRoles = [
  'Owner',
  'Sales Rep',
  'Sales Engineer',
  'Account Executive',
  'Solution Architect',
  'Executive Sponsor',
  'Technical Lead',
  'Support'
];

// Currency Options
export const Currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee' }
];

// Billing Frequencies
export const BillingFrequencies = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMI_ANNUALLY', label: 'Semi-Annually' },
  { value: 'ANNUALLY', label: 'Annually' },
  { value: 'ONE_TIME', label: 'One-Time' }
];

// Units of Measure
export const UnitsOfMeasure = [
  'Each',
  'Hour',
  'Day',
  'Week',
  'Month',
  'Year',
  'License',
  'Seat',
  'User',
  'GB',
  'TB',
  'Unit',
  'Pack'
];

// Loss Reasons
export const LossReasons = [
  'Price',
  'Competitor',
  'No Budget',
  'No Decision',
  'Timing',
  'Feature Gap',
  'Lost Contact',
  'Internal Solution',
  'Acquired',
  'Other'
];

// Win Reasons
export const WinReasons = [
  'Price',
  'Features',
  'Relationship',
  'Support',
  'Integration',
  'Reputation',
  'Demo',
  'Reference',
  'Other'
];

// Contact Roles for Opportunity
export const ContactRoles = [
  'Decision Maker',
  'Influencer',
  'Technical Evaluator',
  'Economic Buyer',
  'End User',
  'Champion',
  'Gatekeeper',
  'Executive Sponsor',
  'Project Manager',
  'Other'
];

// Stagnant Deal Thresholds (days)
export const StagnantDealThresholds = {
  WARNING: 14,   // 14 days - yellow warning
  CRITICAL: 30   // 30 days - red alert
};

// Helper Functions
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  const currencyInfo = Currencies.find(c => c.code === currency) || Currencies[0];
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${value}%`;
};

export const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US').format(value);
};

export const isQuoteEditable = (status) => {
  return [QuoteStatus.DRAFT, QuoteStatus.REJECTED].includes(status);
};

// Check if quote can be sent (must be DRAFT or APPROVED, not PENDING_APPROVAL)
export const isQuoteSendable = (status) => {
  return [QuoteStatus.DRAFT, QuoteStatus.APPROVED].includes(status);
};

// Check if quote requires approval
export const requiresApproval = (quote) => {
  return quote?.status === QuoteStatus.PENDING_APPROVAL || quote?.requires_approval;
};

// Check if user can approve/reject a quote
export const canApproveQuote = (status) => {
  return status === QuoteStatus.PENDING_APPROVAL;
};

export const isOpportunityClosed = (stage) => {
  return [OpportunityStage.CLOSED_WON, OpportunityStage.CLOSED_LOST].includes(stage);
};

export const getStageIndex = (stage) => {
  return AllStages.indexOf(stage);
};

export const canProgressToStage = (currentStage, targetStage) => {
  // Allow moving forward or backward in pipeline
  // But once closed, cannot change
  if (isOpportunityClosed(currentStage)) return false;
  return true;
};

// Lead Source (imported from CRM for reference)
export const LeadSource = {
  WEB_FORM: 'WEB_FORM',
  MANUAL_ENTRY: 'MANUAL_ENTRY',
  BUSINESS_CARD: 'BUSINESS_CARD',
  REFERRAL: 'REFERRAL',
  SOCIAL_MEDIA: 'SOCIAL_MEDIA',
  EMAIL_CAMPAIGN: 'EMAIL_CAMPAIGN',
  CHATBOT: 'CHATBOT',
  OTHER: 'OTHER'
};

export const LeadSourceLabels = {
  WEB_FORM: 'Web Form',
  MANUAL_ENTRY: 'Manual Entry',
  BUSINESS_CARD: 'Business Card',
  REFERRAL: 'Referral',
  SOCIAL_MEDIA: 'Social Media',
  EMAIL_CAMPAIGN: 'Email Campaign',
  CHATBOT: 'Chatbot',
  OTHER: 'Other'
};

// Alias exports for component compatibility
export const QUOTE_STATUS_LABELS = QuoteStatusLabels;
export const QUOTE_STATUS_COLORS = QuoteStatusColors;
export const CURRENCIES = Currencies;
export const BILLING_FREQUENCIES = BillingFrequencies;
export const TARGET_PERIOD_LABELS = TargetPeriodLabels;
export const STAGE_LABELS = OpportunityStageLabels;
export const STAGE_COLORS = OpportunityStageColors;
export const STAGE_PROBABILITIES = StageProbabilities;
export const OPPORTUNITY_TYPE_LABELS = OpportunityTypeLabels;
export const FORECAST_CATEGORY_LABELS = ForecastCategoryLabels;
export const FORECAST_CATEGORY_COLORS = ForecastCategoryColors;
export const PRODUCT_TYPE_LABELS = ProductTypeLabels;
export const PRODUCT_STATUS_LABELS = ProductStatusLabels;
export const PRODUCT_STATUS_COLORS = ProductStatusColors;
export const LEAD_SOURCE_LABELS = LeadSourceLabels;
export const CONTACT_ROLES = ContactRoles;
export const STAGNANT_THRESHOLDS = StagnantDealThresholds;
export const VISIBILITY_SCOPE = VisibilityScope;
export const VISIBILITY_SCOPE_LABELS = VisibilityScopeLabels;
export const VISIBILITY_SCOPE_COLORS = VisibilityScopeColors;
export const VIEW_FILTER_OPTIONS = ViewFilterOptions;

// Calculate days in current stage from stage_history
export const calculateDaysInStage = (stageHistory, currentStage) => {
  if (!stageHistory || stageHistory.length === 0) return 0;

  // Find the most recent entry for the current stage
  const sortedHistory = [...stageHistory].sort((a, b) =>
    new Date(b.date) - new Date(a.date)
  );

  const lastStageChange = sortedHistory.find(h => h.to_stage === currentStage);

  if (lastStageChange && lastStageChange.date) {
    const changeDate = new Date(lastStageChange.date);
    const today = new Date();
    const diffTime = Math.abs(today - changeDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  return 0;
};

// Get stagnant status based on days in stage
export const getStagnantStatus = (daysInStage) => {
  if (daysInStage >= StagnantDealThresholds.CRITICAL) {
    return { level: 'critical', color: 'error', message: `Stagnant for ${daysInStage} days` };
  } else if (daysInStage >= StagnantDealThresholds.WARNING) {
    return { level: 'warning', color: 'warning', message: `${daysInStage} days in stage` };
  }
  return null;
};